import React from 'react'
import { Chat } from '@/config'
import { Icon, View } from '@/components'
import { onUpdate } from '@codeleap/hooks'
import { MessageStatusTypes } from '../types'
import { MessageContextValue, useChannelStateContext, useMessageContext } from 'stream-chat-react'
import { useMessageReceiptStage, type ChatMessageStage } from '@packages/chat'

export type MessageStatusPropsWithContext = MessageContextValue & {
  lastOwnMessageReadDate?: Date | null
}

const statusConstants = Chat.Constants.messageStatus

const MessageStatusWithContext = (props: MessageStatusPropsWithContext) => {
  const user = Chat.Store.useUser()
  const {
    isMyMessage,
    lastOwnMessage,
    message,
    readBy,
    threadList,
    lastOwnMessageReadDate,
  } = props

  const mine = isMyMessage()
  const justReadByMe = readBy?.length === 1 && readBy[0].id === user?.id
  const read = !!(readBy?.length && !justReadByMe && !threadList)

  // Real Stream delivery receipt — replaces the old `status === 'received'`
  // heuristic (Stream-server ACK), which made the single "sent" check dead
  // code and showed the grey double-check before the recipient received it.
  const { channel } = useChannelStateContext('MessageStatus')
  const stage = useMessageReceiptStage({
    channel,
    message,
    currentUserId: user?.id,
    isMine: mine,
    readByOther: read,
  })

  if (!mine || message.status === MessageStatusTypes.FAILED || message.type === 'error') return null

  const isLastOwnMessage = lastOwnMessage?.id === message.id

  if (stage === 'sending') return <SingleCheckIcon state='sending' />
  if (stage === 'sent') return <SingleCheckIcon state='sent' />
  if (stage === 'delivered' || stage === 'read' || read) {
    return (
      <DoubleCheckIcon
        stage={stage}
        lastOwnMessageReadDate={lastOwnMessageReadDate}
        message={message}
        read={read}
        isLastOwnMessage={isLastOwnMessage}
        lastOwnMessage={lastOwnMessage}
      />
    )
  }

  return null
}

// `state` is stamped as a data-testid (message-status:<state>) so e2e can assert
// the receipt stage without reading the colour token (FR-0052) — delivered vs
// read differ only by colour, which is brittle to inspect in the DOM.
const Wrapper = ({ children, state }: { children: React.ReactNode; state: ChatMessageStage }) => (
  <View debugName={`message-status:${state}`} style={['ml:0.3']}>{children}</View>
)

const SingleCheckIcon = ({ state }: { state: ChatMessageStage }) => (
  <Wrapper state={state}><Icon name='check' style={['chatMessage', 'color:neutralSolid500']} /></Wrapper>
)

const DoubleCheckIcon = ({ stage, lastOwnMessageReadDate, message, read, isLastOwnMessage, lastOwnMessage }) => {
  const isNewerThanLastOwnMessage = lastOwnMessageReadDate && new Date(lastOwnMessageReadDate) < new Date(message.created_at)

  // The double check shows the read colour when the shared receipt hook reports
  // `read` (the tracker-driven signal from PR 1487, so the blue check advances
  // live on `message.read` for every message type), or — as a fallback for the
  // web `readBy` path — when readBy includes another user, or for an older own
  // message (not the last, not newer than the last read). OR-ing the shared
  // signal in is what closes the web gap without regressing the existing path.
  const showsRead = stage === 'read' || read || (!isLastOwnMessage && !isNewerThanLastOwnMessage)
  const state: ChatMessageStage = showsRead ? 'read' : 'delivered'

  function getColor() {
    if (showsRead) return statusConstants.read.color
    return statusConstants.delivered.color
  }

  onUpdate(() => {
    if (isLastOwnMessage && read) { Chat.Store.setLastOwnMessageRead(lastOwnMessage?.created_at) }
  }, [read, isLastOwnMessage, lastOwnMessage?.created_at])

  return <Wrapper state={state}><Icon name='checks' style={['chatMessage', getColor()]} /></Wrapper>
}

const areEqual = (
  prevProps: MessageStatusPropsWithContext,
  nextProps: MessageStatusPropsWithContext,
) => {
  const { message: prevMessage, readBy: prevReadBy, lastOwnMessageReadDate: prevLastOwnMessageReadDate } = prevProps
  const { message: nextMessage, readBy: nextReadBy, lastOwnMessageReadDate: nextLastOwnMessageReadDate } = nextProps

  if (prevMessage?.type !== nextMessage?.type) return false

  if (prevMessage?.status !== nextMessage?.status) return false

  const readByEqual = JSON.stringify(prevReadBy) === JSON.stringify(nextReadBy)
  if (!readByEqual) return false

  if (prevLastOwnMessageReadDate !== nextLastOwnMessageReadDate) return false

  return true
}

const MemoizedMessageStatus = React.memo(
  MessageStatusWithContext,
  areEqual,
) as typeof MessageStatusWithContext

export type MessageStatusProps = Partial<MessageStatusPropsWithContext>

export const MessageStatus = () => {
  const messageProps = useMessageContext('MessageStatus')
  const lastOwnMessageReadDate = Chat.Store.state.use(s => s.lastOwnMessageReadDate)
  return <MemoizedMessageStatus {...messageProps} lastOwnMessageReadDate={lastOwnMessageReadDate} />
}

MessageStatus.displayName = 'MessageStatus{messageSimple{status}}'
